import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Download, Smartphone, X } from 'lucide-react';
import { trackPWAInstall } from '@/lib/simpleAnalytics';
import jewmapLogo from '@/assets/jewmap-logo.png';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface InstallPromptModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function InstallPromptModal({ open, onOpenChange }: InstallPromptModalProps) {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [platform, setPlatform] = useState<'ios' | 'android' | 'desktop'>('desktop');
  const [neverAsk, setNeverAsk] = useState(false);

  useEffect(() => {
    // Detect platform
    const userAgent = navigator.userAgent.toLowerCase();
    if (/iphone|ipad|ipod/.test(userAgent)) {
      setPlatform('ios');
    } else if (/android/.test(userAgent)) {
      setPlatform('android');
    } else {
      setPlatform('desktop');
    }

    // Listen for beforeinstallprompt event
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  useEffect(() => {
    if (open) {
      trackPWAInstall('prompt_shown', platform);
    }
  }, [open, platform]);

  const handleInstall = async () => {
    if (deferredPrompt) {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        trackPWAInstall('accepted', platform);
        localStorage.setItem('jewmap_pwa_installed', 'true');
        onOpenChange(false);
      } else {
        trackPWAInstall('dismissed', platform);
      }
      setDeferredPrompt(null);
    }
  };

  const handleClose = () => {
    if (neverAsk) {
      localStorage.setItem('jewmap_never_ask_install', 'true');
      trackPWAInstall('never_ask', platform);
    } else {
      trackPWAInstall('dismissed', platform);
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="flex flex-col items-center text-center">
          <div className="w-16 h-16 mb-4">
            <img src={jewmapLogo} alt="JewMap" width={64} height={64} className="w-full h-full object-contain" />
          </div>
          <DialogTitle className="text-xl">Installez JewMap</DialogTitle>
          <DialogDescription className="text-center">
            Accédez à JewMap directement depuis votre écran d'accueil
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Benefits */}
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-center gap-2">
              <Smartphone className="h-4 w-4 text-primary" />
              Accès rapide depuis l'écran d'accueil
            </li>
            <li className="flex items-center gap-2">
              <Download className="h-4 w-4 text-primary" />
              Fonctionne hors-ligne
            </li>
          </ul>

          {/* Platform-specific instructions */}
          {platform === 'ios' ? (
            <div className="rounded-lg bg-muted p-4 text-sm">
              <p className="font-medium mb-2">Pour installer sur iPhone/iPad :</p>
              <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                <li>Appuyez sur le bouton <strong>Partager</strong> (⎙) en bas de Safari</li>
                <li>Faites défiler et appuyez sur <strong>"Sur l'écran d'accueil"</strong></li>
                <li>Confirmez en appuyant sur <strong>"Ajouter"</strong></li>
              </ol>
            </div>
          ) : deferredPrompt ? (
            <Button onClick={handleInstall} className="w-full" size="lg">
              <Download className="mr-2 h-5 w-5" />
              Installer l'application
            </Button>
          ) : (
            <div className="rounded-lg bg-muted p-4 text-sm">
              <p className="font-medium mb-2">Pour installer :</p>
              <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                <li>Ouvrez le menu de votre navigateur (⋮)</li>
                <li>Sélectionnez <strong>"Installer l'application"</strong> ou <strong>"Ajouter à l'écran d'accueil"</strong></li>
              </ol>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-4">
          {/* Never ask checkbox */}
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="neverAsk" 
              checked={neverAsk}
              onCheckedChange={(checked) => setNeverAsk(checked === true)}
            />
            <label 
              htmlFor="neverAsk" 
              className="text-sm text-muted-foreground cursor-pointer"
            >
              Ne plus me demander
            </label>
          </div>

          {/* Close button */}
          <Button variant="outline" onClick={handleClose} className="w-full">
            <X className="mr-2 h-4 w-4" />
            Plus tard
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
